import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { aiApi } from "@/services/api/aiApi";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight, X, AlertTriangle, Info, AlertCircle, Sparkles, Shield, FileSearch, BookOpen, Scale, CheckCircle2 } from "lucide-react";

interface AiDocumentAnalysisProps {
  orderId: number;
  orderDocumentId: number;
  associationId?: number;
  managementCompanyId?: number;
  /**
   * Which answer the review dock is looking at, as "<field_id>:<index>", so
   * the page can light it up in the application below. null = dock closed;
   * an anchor of null = a finding about the application as a whole.
   */
  onReviewFocusChange?: (focus: { anchor: string | null } | null) => void;
}

const severityConfig = {
  info: { color: "bg-blue-100 text-blue-800 border-blue-200", icon: Info, label: "Info" },
  warning: { color: "bg-amber-100 text-amber-800 border-amber-200", icon: AlertTriangle, label: "Warning" },
  critical: { color: "bg-red-100 text-red-800 border-red-200", icon: AlertCircle, label: "Critical" },
};

const riskScoreConfig = {
  low: "bg-green-100 text-green-800",
  medium: "bg-amber-100 text-amber-800",
  high: "bg-red-100 text-red-800",
};

const analysisSteps = [
  { icon: FileSearch, label: "Reading application data", duration: 2000 },
  { icon: BookOpen, label: "Loading association rules & regulations", duration: 3000 },
  { icon: Scale, label: "Cross-referencing compliance requirements", duration: 5000 },
  { icon: Shield, label: "Generating compliance report", duration: 4000 },
];

const AiProcessingIndicator = () => {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    let elapsed = 0;
    analysisSteps.forEach((step, index) => {
      if (index > 0) {
        elapsed += analysisSteps[index - 1].duration;
        timers.push(setTimeout(() => setActiveStep(index), elapsed));
      }
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="py-4 space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative h-5 w-5">
          <Sparkles className="h-5 w-5 text-violet-500 animate-pulse" />
          <div className="absolute inset-0 h-5 w-5 text-violet-400 animate-ping opacity-30">
            <Sparkles className="h-5 w-5" />
          </div>
        </div>
        <span className="text-sm font-medium text-violet-700">AI analysis in progress</span>
      </div>

      <div className="space-y-1">
        {analysisSteps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === activeStep;
          const isCompleted = index < activeStep;
          const isPending = index > activeStep;

          return (
            <div
              key={index}
              className={`flex items-center gap-3 rounded-md px-3 py-2 transition-all duration-500 ${
                isActive
                  ? "bg-violet-50 border border-violet-200"
                  : isCompleted
                  ? "opacity-60"
                  : "opacity-30"
              }`}
            >
              <div className="relative flex-shrink-0">
                {isActive ? (
                  <div className="h-4 w-4 rounded-full border-2 border-violet-500 border-t-transparent animate-spin" />
                ) : isCompleted ? (
                  <svg className="h-4 w-4 text-violet-500" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 0a8 8 0 110 16A8 8 0 018 0zm3.78 4.97a.75.75 0 00-1.06 0L7 8.69 5.28 6.97a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.06 0l4.25-4.25a.75.75 0 000-1.06z" />
                  </svg>
                ) : (
                  <Icon className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
              <span
                className={`text-sm ${
                  isActive ? "text-violet-700 font-medium" : isCompleted ? "text-muted-foreground" : "text-muted-foreground"
                }`}
              >
                {step.label}
                {isActive && (
                  <span className="inline-flex ml-1">
                    <span className="animate-[bounce_1.4s_ease-in-out_infinite]">.</span>
                    <span className="animate-[bounce_1.4s_ease-in-out_0.2s_infinite]">.</span>
                    <span className="animate-[bounce_1.4s_ease-in-out_0.4s_infinite]">.</span>
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>

      <div className="h-1.5 bg-violet-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-violet-400 to-violet-600 rounded-full animate-[progress_14s_ease-in-out_forwards]"
          style={{
            animation: "progress 14s ease-in-out forwards",
          }}
        />
      </div>

      <style>{`
        @keyframes progress {
          0% { width: 0%; }
          15% { width: 15%; }
          35% { width: 35%; }
          60% { width: 65%; }
          80% { width: 85%; }
          100% { width: 95%; }
        }
      `}</style>
    </div>
  );
};

/** "<field_id>:<index>" for a finding about one answer; null when it is about the application as a whole. */
const anchorOf = (issue: any): string | null =>
  issue?.field_id != null ? `${issue.field_id}:${issue.field_index ?? 0}` : null;

const severityRank: Record<string, number> = { critical: 0, warning: 1, info: 2 };

/** The backoffice layout scrolls <main>, not the window. */
const scrollContainer = (): HTMLElement | null => document.querySelector("main");

const scrollToAnchor = (anchor: string): boolean => {
  const el = document.querySelector(`[data-ai-anchor="${anchor}"]`);
  el?.scrollIntoView({ behavior: "smooth", block: "center" });
  return !!el;
};

const scrollToFinding = (id: string) => {
  document
    .querySelector(`[data-ai-finding="${id}"]`)
    ?.scrollIntoView({ behavior: "smooth", block: "center" });
};

const scrollPageToTop = () => {
  const main = scrollContainer();
  if (main) main.scrollTo({ top: 0, behavior: "smooth" });
  else window.scrollTo({ top: 0, behavior: "smooth" });
};

/** Where an answer sits on the page, so the review can walk top to bottom. */
const pagePosition = (anchor: string | null): number => {
  if (!anchor) return Number.POSITIVE_INFINITY;
  const el = document.querySelector(`[data-ai-anchor="${anchor}"]`);
  if (!el) return Number.POSITIVE_INFINITY;
  return el.getBoundingClientRect().top + (scrollContainer()?.scrollTop ?? window.scrollY);
};

const resolutionLabel: Record<string, string> = {
  confirmed: "Confirmed",
  dismissed: "Dismissed",
};

export const AiDocumentAnalysis: React.FC<AiDocumentAnalysisProps> = ({
  orderId,
  orderDocumentId,
  onReviewFocusChange,
}) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isExpanded, setIsExpanded] = useState(true);
  // The findings list starts folded: the review walks them one by one, so the
  // full list is there to consult, not to read first.
  const [issuesOpen, setIssuesOpen] = useState(false);
  const queryKey = ["ai-analysis", orderDocumentId];

  // The guided review (5-Oct, same language as the Process screen): the
  // findings in page order, fixed when a pass starts, and the one the
  // floating dock is showing.
  const [walk, setWalk] = useState<string[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  const { data: analysis, isLoading } = useQuery({
    queryKey,
    queryFn: () => aiApi.getOrderDocumentAnalysis(orderDocumentId),
  });

  const analyzeMutation = useMutation({
    mutationFn: () => aiApi.analyzeOrderDocument(orderId, orderDocumentId),
    onMutate: () => {
      // A new run brings new findings; whatever the dock was on is gone. Open
      // the panel so the run's progress is visible even if it was folded.
      setActiveId(null);
      setWalk([]);
      setIsExpanded(true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (v: { id: string; resolution: "confirmed" | "dismissed" | null }) =>
      aiApi.resolveComplianceIssue(orderDocumentId, v.id, v.resolution),
    onSuccess: (updated) => {
      if (updated) queryClient.setQueryData(queryKey, updated);
    },
    onError: (err) => {
      queryClient.invalidateQueries({ queryKey });
      toast({
        variant: "destructive",
        title: "Could not save that decision",
        description: err instanceof Error ? err.message : "Please try again.",
      });
    },
  });

  const hasAnalysis = analysis && analysis.status === "completed";
  const isProcessing = analysis?.status === "processing" || analyzeMutation.isPending;

  // Auto-trigger analysis if no previous analysis exists
  const autoTriggered = useRef(false);
  useEffect(() => {
    if (!isLoading && !analysis && !autoTriggered.current) {
      autoTriggered.current = true;
      analyzeMutation.mutate();
    }
  }, [isLoading, analysis]);

  const issues: any[] = hasAnalysis ? analysis.compliance_issues ?? [] : [];
  // Findings written before the guided review existed carry no id. They still
  // show; they just cannot be walked until the analysis is re-run.
  const reviewable = useMemo(() => issues.filter((i) => typeof i?.id === "string"), [issues]);
  const byId = useMemo(() => new Map(reviewable.map((i) => [i.id, i])), [reviewable]);
  const pendingCount = reviewable.filter((i) => !i.resolution).length;
  const settledCount = reviewable.length - pendingCount;
  const allSettled = reviewable.length > 0 && pendingCount === 0;

  // A fully reviewed analysis opens folded: the "Reviewed" badge says what
  // matters, and the details are one click away. Only on the first load - a
  // re-run the operator asked for stays open.
  const foldedOnLoad = useRef(false);
  useEffect(() => {
    if (foldedOnLoad.current || !hasAnalysis) return;
    foldedOnLoad.current = true;
    if (allSettled) setIsExpanded(false);
  }, [hasAnalysis, allSettled]);

  const activeIssue = activeId ? byId.get(activeId) ?? null : null;
  const activeAnchor = activeIssue ? anchorOf(activeIssue) : null;
  const showIssues = issuesOpen || (!!activeIssue && !activeAnchor);

  // Tell the page which answer to light up.
  const focusKey = activeIssue ? `${activeIssue.id}|${activeAnchor ?? ""}` : "";
  useEffect(() => {
    onReviewFocusChange?.(activeIssue ? { anchor: activeAnchor } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey]);
  useEffect(() => () => onReviewFocusChange?.(null), []);

  // Esc closes the dock without deciding anything.
  useEffect(() => {
    if (!activeId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActiveId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeId]);

  const goTo = (id: string) => {
    setActiveId(id);
    const anchor = anchorOf(byId.get(id));
    // Let the highlight render first, then bring the answer to the middle of
    // the screen; a finding about the whole application shows its card.
    setTimeout(() => {
      if (!anchor || !scrollToAnchor(anchor)) scrollToFinding(id);
    }, 60);
  };

  /** Fix the order once: top of the page to the bottom, whole-application findings last, most severe first. */
  const startReview = (fromId?: string) => {
    setIsExpanded(true);
    const ordered = [...reviewable].sort((a, b) => {
      const pa = pagePosition(anchorOf(a));
      const pb = pagePosition(anchorOf(b));
      if (pa !== pb) {
        if (!Number.isFinite(pa)) return 1;
        if (!Number.isFinite(pb)) return -1;
        return pa - pb;
      }
      return (severityRank[a.severity] ?? 3) - (severityRank[b.severity] ?? 3);
    });
    setWalk(ordered.map((i) => i.id));
    const target = fromId ?? ordered.find((i) => !i.resolution)?.id;
    if (target) goTo(target);
  };

  const finish = (remaining: number) => {
    setActiveId(null);
    scrollPageToTop();
    // Everything decided: fold the panel, leaving the "Reviewed" badge.
    if (remaining === 0) setIsExpanded(false);
    toast(
      remaining === 0
        ? {
            title: "Review complete",
            description: "Every finding is settled. The application is ready for your decision.",
          }
        : {
            title: "End of the list",
            description: `${remaining} ${remaining === 1 ? "finding" : "findings"} skipped for now — Continue review goes back to them.`,
          }
    );
  };

  /** Move past `fromId` to the next finding still waiting for a decision. */
  const advance = (fromId: string, justDecided?: string) => {
    const order = walk.length ? walk : reviewable.map((i) => i.id);
    const waiting = (id: string) => id !== justDecided && !byId.get(id)?.resolution;
    const next = order.slice(order.indexOf(fromId) + 1).find(waiting);
    if (next) goTo(next);
    else finish(order.filter(waiting).length);
  };

  const setResolutionLocally = (id: string, resolution: string | null) =>
    queryClient.setQueryData(queryKey, (old: any) =>
      old
        ? {
            ...old,
            compliance_issues: (old.compliance_issues ?? []).map((i: any) =>
              i.id === id
                ? { ...i, resolution, resolved_at: resolution ? new Date().toISOString() : null }
                : i
            ),
          }
        : old
    );

  /** The dock moves on at once; the server confirms the decision behind it. */
  const decide = (id: string, resolution: "confirmed" | "dismissed") => {
    setResolutionLocally(id, resolution);
    resolveMutation.mutate({ id, resolution });
    advance(id, id);
  };

  const undo = (id: string) => {
    setResolutionLocally(id, null);
    resolveMutation.mutate({ id, resolution: null });
  };

  const walkIndex = activeId ? walk.indexOf(activeId) : -1;
  const activeConfig = activeIssue
    ? severityConfig[activeIssue.severity as keyof typeof severityConfig] || severityConfig.info
    : null;
  const ActiveIcon = activeConfig?.icon;

  return (
    <div className="rounded-lg border bg-card">
      <div
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          {isProcessing ? (
            <div className="relative h-5 w-5">
              <Sparkles className="h-5 w-5 text-violet-500 animate-pulse" />
            </div>
          ) : (
            <Sparkles className="h-5 w-5 text-violet-500" />
          )}
          <h3 className="font-semibold">AI Compliance Analysis</h3>
          {hasAnalysis && analysis.risk_score && (
            <Badge className={riskScoreConfig[analysis.risk_score as keyof typeof riskScoreConfig] || ""}>
              {analysis.risk_score.toUpperCase()} RISK
            </Badge>
          )}
          {allSettled && !isProcessing && (
            <Badge
              className="gap-1 border-green-200 bg-green-100 text-green-800 hover:bg-green-100"
              title={`All ${settledCount} findings reviewed`}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Reviewed
            </Badge>
          )}
          {isProcessing && (
            <Badge className="bg-violet-100 text-violet-700 border-violet-200 animate-pulse">
              Analyzing
            </Badge>
          )}
          {analysis?.status === "failed" && (
            <Badge variant="destructive">Failed</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isProcessing && hasAnalysis && (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                analyzeMutation.mutate();
              }}
            >
              <Sparkles className="h-3 w-3 mr-1" />
              Re-run Analysis
            </Button>
          )}
          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </div>

      {isExpanded && (
        <div className="border-t px-4 pb-4 pt-3 space-y-4">
          {isLoading && (
            <div className="flex items-center gap-2 text-muted-foreground py-4">
              <div className="h-4 w-4 rounded-full border-2 border-violet-500 border-t-transparent animate-spin" />
              Loading analysis...
            </div>
          )}

          {isProcessing && <AiProcessingIndicator />}

          {analyzeMutation.isError && (
            <div className="text-sm text-red-600 bg-red-50 rounded p-3">
              Analysis failed: {(analyzeMutation.error as Error)?.message || "Unknown error"}
            </div>
          )}

          {analysis?.status === "failed" && !isProcessing && (
            <div className="text-sm text-red-600 bg-red-50 rounded p-3">
              Previous analysis failed: {analysis.error_message || "Unknown error"}
            </div>
          )}

          {hasAnalysis && !isProcessing && (
            <>
              {/* Summary */}
              {analysis.summary && (
                <div className="text-sm text-muted-foreground">
                  {analysis.summary}
                </div>
              )}

              {/* The guided review: walk the findings one by one, each next
                  to the answer it is about. */}
              {reviewable.length > 0 && (
                <div className="rounded-md border border-violet-200 bg-violet-50/70 p-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-violet-900">
                    <Sparkles className="h-4 w-4 flex-shrink-0" />
                    AI review
                    {pendingCount > 0 ? (
                      <>
                        <span className="font-normal text-violet-700">
                          · {pendingCount} {pendingCount === 1 ? "finding" : "findings"} to review
                          {settledCount > 0 && <> · {settledCount} settled</>}
                        </span>
                        <button
                          type="button"
                          className="ml-1 rounded border border-violet-300 px-2 py-0.5 text-xs font-semibold text-violet-800 hover:bg-violet-100"
                          onClick={() => (activeId ? goTo(activeId) : startReview())}
                        >
                          {activeId ? "Continue review" : "Review"}
                        </button>
                      </>
                    ) : (
                      <span className="font-normal text-violet-700">
                        · all {settledCount} settled ✓
                      </span>
                    )}
                  </div>
                </div>
              )}

              {issues.length > 0 && reviewable.length === 0 && (
                <div className="text-xs text-muted-foreground">
                  Re-run the analysis to review these findings one by one, next to the answers they are about.
                </div>
              )}

              {/* Compliance Issues - folded until asked for. A finding about
                  the application as a whole opens it while the dock is on it,
                  because its card is what the review points at. */}
              {issues.length > 0 && (
                <div className="space-y-2">
                  <button
                    type="button"
                    className="flex items-center gap-1 text-sm font-medium hover:text-foreground/80"
                    onClick={() => setIssuesOpen(!issuesOpen)}
                  >
                    {showIssues ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    Compliance Issues ({issues.length})
                  </button>
                  {showIssues && issues.map((issue: any, index: number) => {
                    const config = severityConfig[issue.severity as keyof typeof severityConfig] || severityConfig.info;
                    const Icon = config.icon;
                    const canWalk = typeof issue?.id === "string";
                    const isActive = canWalk && issue.id === activeId;
                    return (
                      <div
                        key={issue.id ?? index}
                        data-ai-finding={issue.id}
                        onClick={canWalk ? () => (walk.length ? goTo(issue.id) : startReview(issue.id)) : undefined}
                        className={
                          `rounded-md border p-3 ${config.color}` +
                          (canWalk ? " cursor-pointer" : "") +
                          (isActive ? " ring-2 ring-violet-400" : "") +
                          (issue.resolution === "dismissed" ? " opacity-60" : "")
                        }
                      >
                        <div className="flex items-start gap-2">
                          <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <div className="min-w-0 flex-1 space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold uppercase">{config.label}</span>
                              {issue.rule_reference && (
                                <span className="text-xs opacity-75">- {issue.rule_reference}</span>
                              )}
                              {canWalk && issue.resolution && (
                                <span className="ml-auto rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-semibold">
                                  {resolutionLabel[issue.resolution] ?? issue.resolution}
                                </span>
                              )}
                            </div>
                            <p className="text-sm">{issue.description}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {analysis.compliance_issues?.length === 0 && (
                <div className="text-sm text-green-700 bg-green-50 rounded-md p-3">
                  No compliance issues found.
                </div>
              )}

              {/* Suggestions */}
              {analysis.suggestions && analysis.suggestions.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Suggestions</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    {analysis.suggestions.map((suggestion: string, index: number) => (
                      <li key={index}>{suggestion}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {!hasAnalysis && !isLoading && !isProcessing && !analysis?.status && (
            <p className="text-sm text-muted-foreground py-2">
              AI analysis will start automatically...
            </p>
          )}
        </div>
      )}

      {/* The floating review dock: the decision happens here, at the bottom
          of the screen, while the answer it is about is lit up above. */}
      {activeIssue && activeConfig && ActiveIcon && (
        <div className="pointer-events-none fixed inset-x-0 bottom-5 z-50 flex justify-center px-4">
          <div className="pointer-events-auto w-full max-w-xl rounded-lg border border-violet-300 bg-white shadow-2xl">
            <div className="flex items-center gap-2 rounded-t-lg border-b border-violet-100 bg-violet-50/80 px-3 py-1.5">
              <Sparkles className="h-4 w-4 flex-shrink-0 text-violet-700" />
              <span className="text-xs font-semibold text-violet-900">AI review</span>
              <span className="text-xs text-violet-600">
                · {walkIndex + 1} of {walk.length}
              </span>
              <div className="ml-auto flex items-center gap-0.5">
                <button
                  type="button"
                  title="Previous"
                  disabled={walkIndex <= 0}
                  className="rounded p-1 text-gray-600 hover:bg-gray-100 disabled:cursor-default disabled:text-gray-300"
                  onClick={() => walkIndex > 0 && goTo(walk[walkIndex - 1])}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  title="Next"
                  disabled={walkIndex < 0 || walkIndex >= walk.length - 1}
                  className="rounded p-1 text-gray-600 hover:bg-gray-100 disabled:cursor-default disabled:text-gray-300"
                  onClick={() => walkIndex < walk.length - 1 && goTo(walk[walkIndex + 1])}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  title="Close (Esc)"
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  onClick={() => setActiveId(null)}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="px-3 py-2">
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] font-semibold uppercase ${activeConfig.color}`}
                >
                  <ActiveIcon className="h-3 w-3" />
                  {activeConfig.label}
                </span>
                <button
                  type="button"
                  className="min-w-0 truncate text-sm font-medium text-gray-900 underline-offset-2 hover:underline"
                  title={activeAnchor ? "Show the answer" : "Show the finding"}
                  onClick={() => goTo(activeIssue.id)}
                >
                  {activeIssue.rule_reference || "Finding"}
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-700">{activeIssue.description}</p>
              {!activeAnchor && (
                <p className="mt-1 text-xs text-gray-500">About the application as a whole.</p>
              )}
            </div>

            <div className="flex items-center gap-2 px-3 pb-2.5">
              {activeIssue.resolution ? (
                <>
                  <span className="text-sm font-medium text-violet-800">
                    {resolutionLabel[activeIssue.resolution] ?? activeIssue.resolution}
                  </span>
                  <button
                    type="button"
                    className="text-xs text-gray-500 underline decoration-dotted hover:text-gray-700"
                    onClick={() => undo(activeIssue.id)}
                  >
                    Undo
                  </button>
                </>
              ) : (
                <>
                  <Button
                    size="sm"
                    className="h-7 bg-violet-600 px-3 text-xs hover:bg-violet-700"
                    onClick={() => decide(activeIssue.id, "confirmed")}
                  >
                    Confirm
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-3 text-xs"
                    onClick={() => decide(activeIssue.id, "dismissed")}
                  >
                    Dismiss
                  </Button>
                </>
              )}
              <button
                type="button"
                className="ml-auto text-xs text-gray-500 underline decoration-dotted hover:text-gray-700"
                onClick={() => advance(activeIssue.id)}
              >
                {activeIssue.resolution ? "Next" : "Skip for now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
